import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import PropTypes from 'prop-types';
import { UserContext } from '../contexts/User';
import { sendRequest } from '../api/shootAPI';
import API from '../api/endpoints';

/**
 * Banner component that displays:
 * 1. When workspace has no active subscription
 * 2. When workspace subscription is expiring soon (3 days or less)
 * @param {boolean} inline - If true, renders compact inline version for navbar
 */
function SubscriptionBanner({ inline = false }) {
  const { activeWorkspace, user } = useContext(UserContext);
  const [expiryInfo, setExpiryInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExpiryInfo = async () => {
      if (!activeWorkspace || !activeWorkspace.membership) {
        setLoading(false);
        return;
      }

      try {
        const response = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.getPlanExpiry(activeWorkspace.id),
        });
        setExpiryInfo(response.data);
      } catch (error) {
        console.error('Error fetching plan expiry info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchExpiryInfo();
  }, [activeWorkspace]);

  // Don't show anything while loading
  if (loading) {
    return null;
  }

  // Only show no-subscription banner if workspace has no subscription
  if (!activeWorkspace || !activeWorkspace.membership) {
    if (inline) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <span style={{ fontSize: '16px' }}>💡</span>
          <span style={{ color: '#856404', fontWeight: 500, fontSize: '14px' }}>
            Free plan - Upgrade to unlock all features
          </span>
          <Link to="/admin/settings/payments" style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#53A551',
                color: '#FFFFFF',
                textTransform: 'none',
                fontSize: '12px',
                padding: '4px 12px',
                '&:hover': {
                  backgroundColor: '#448843',
                },
                whiteSpace: 'nowrap',
              }}
            >
              Upgrade
            </Button>
          </Link>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          backgroundColor: '#FFF8E1',
          borderBottom: '1px solid #FFD54F',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: '20px' }}>💡</span>
          <span style={{ color: '#856404', fontWeight: 500 }}>
            You&apos;re currently on the Free plan.
            Upgrade to unlock all features and get the most out of Shoot!
          </span>
        </Box>
        <Link to="/admin/settings/payments" style={{ textDecoration: 'none' }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: '#53A551',
              color: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#448843',
              },
              whiteSpace: 'nowrap',
            }}
          >
            View Plans
          </Button>
        </Link>
      </Box>
    );
  }

  // Check if user is the primary owner
  const isPrimaryOwner = user && activeWorkspace && user.id === activeWorkspace.ownerId;

  // Show expiry warning only to primary owner when plan is expiring soon
  if (isPrimaryOwner && expiryInfo && expiryInfo.isExpiringSoon && !expiryInfo.isExpired) {
    if (inline) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <span style={{ fontSize: '16px' }}>⚠️</span>
          <span style={{ color: '#C62828', fontWeight: 500, fontSize: '14px' }}>
            Your subscription expires in
            {' '}
            {expiryInfo.daysUntilExpiry}
            {' '}
            day
            {expiryInfo.daysUntilExpiry > 1 ? 's' : ''}
          </span>
          <Link to="/admin/settings/payments?tab=1&renew=true" style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#EF5350',
                color: '#FFFFFF',
                textTransform: 'none',
                fontSize: '12px',
                padding: '4px 12px',
                '&:hover': {
                  backgroundColor: '#C62828',
                },
                whiteSpace: 'nowrap',
              }}
            >
              Renew
            </Button>
          </Link>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          backgroundColor: '#FFEBEE',
          borderBottom: '1px solid #EF5350',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <span style={{ color: '#C62828', fontWeight: 500 }}>
            Your subscription plan is expiring in
            {' '}
            {expiryInfo.daysUntilExpiry}
            {' '}
            day(s).
            Renew now to avoid service interruption!
          </span>
        </Box>
        <Link to="/admin/settings/payments?tab=1&renew=true" style={{ textDecoration: 'none' }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: '#EF5350',
              color: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#C62828',
              },
              whiteSpace: 'nowrap',
            }}
          >
            Renew Plan
          </Button>
        </Link>
      </Box>
    );
  }

  // Show expired warning only to primary owner
  if (isPrimaryOwner && expiryInfo && expiryInfo.isExpired) {
    if (inline) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <span style={{ fontSize: '16px' }}>❌</span>
          <span style={{ color: '#B71C1C', fontWeight: 600, fontSize: '14px' }}>
            Subscription expired
          </span>
          <Link to="/admin/settings/payments?tab=1&renew=true" style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#E53935',
                color: '#FFFFFF',
                textTransform: 'none',
                fontSize: '12px',
                padding: '4px 12px',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#B71C1C',
                },
                whiteSpace: 'nowrap',
              }}
            >
              Renew Now
            </Button>
          </Link>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          backgroundColor: '#FFCDD2',
          borderBottom: '1px solid #E53935',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: '20px' }}>❌</span>
          <span style={{ color: '#B71C1C', fontWeight: 600 }}>
            Your subscription has expired. Renew now to restore access to all features!
          </span>
        </Box>
        <Link to="/admin/settings/payments?tab=1&renew=true" style={{ textDecoration: 'none' }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: '#E53935',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#B71C1C',
              },
              whiteSpace: 'nowrap',
            }}
          >
            Renew Now
          </Button>
        </Link>
      </Box>
    );
  }

  return null;
}

SubscriptionBanner.propTypes = {
  inline: PropTypes.bool,
};

export default SubscriptionBanner;
